<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Masthead -->
      <div class="masthead">
        <div class="mh-top">
          <span class="mh-date">{{ todayDate }}</span>
          <div class="mh-title-col">
            <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="mh-logo" />
            <div class="mh-sub">MORTGAGE & REAL ESTATE PARTNERSHIP</div>
          </div>
  
        </div>
        <div class="mh-rule-group">
          <div class="mhr mhr-thick"></div>
          <div class="mhr mhr-thin"></div>
          <div class="mhr mhr-thick"></div>
        </div>
      </div>

      <!-- Newspaper columns grid -->
      <main class="paper-grid">

        <!-- Col 1 — main headline -->
        <article class="col-main">
          <div class="col-kicker">EXCLUSIVE PARTNERSHIP</div>
          <div class="col-hed-rule"></div>
          <h1 class="paper-h1" v-html="heroH1Html"></h1>
          <div class="col-byline">By {{ loName }} &amp; {{ realtorName }}</div>
          <div class="col-rule"></div>
          <p class="paper-p">{{ heroBody }}</p>
          <div class="paper-btns">
            <a class="pp-btn" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="pp-btn-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
        </article>

        <!-- Vertical rule -->
        <div class="vert-rule"></div>

        <!-- Col 2 — LO profile -->
        <aside class="col-profile">
          <div class="cp-section-head">Loan Officer</div>
          <div class="cp-photo-block lo-photo-block">
            <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="cp-photo" />
            <div class="cp-caption">{{ loName }}, {{ loTitle }}</div>
          </div>
          <p class="cp-bio">
            <strong>{{ loName }}</strong> — {{ loTitle }}<span v-if="loinfo && loinfo.nmls">, NMLS {{ (loinfo.nmls || "").trim() }}</span>
          </p>
          <div class="cp-contacts">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="cp-link lo-cp-link">☎ {{ loinfo.phone_number }}</a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="cp-link lo-cp-link">✉ {{ loinfo.email }}</a>
          </div>
      
        </aside>

        <!-- Vertical rule -->
        <div class="vert-rule"></div>

        <!-- Col 3 — Partner profile -->
        <aside class="col-profile">
          <div class="cp-section-head re-head">Partner</div>
          <div class="cp-photo-block re-photo-block">
            <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="cp-photo" />
            <div class="cp-caption">{{ realtorName }}</div>
          </div>
          <p class="cp-bio">
            <strong>{{ realtorName }}</strong><span v-if="realtor && realtor.brokerage_name"> — {{ realtor.brokerage_name }}</span>
          </p>
          <div class="cp-contacts">
            <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="cp-link re-cp-link">☎ {{ realtor.phone_number }}</a>
            <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="cp-link re-cp-link">✉ {{ realtor.email }}</a>
            <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="cp-link re-cp-link" target="_blank" rel="noreferrer">⬡ Website</a>
          </div>
        </aside>

      </main>

      <!-- Footer rule + credits -->
      <footer class="paper-footer">
        <div class="pf-rules">
          <div class="mhr mhr-thick"></div>
          <div class="mhr mhr-thin"></div>
        </div>
        <div class="pf-credits">
          <span>{{ loName }} · {{ loTitle }}</span>
          <span v-if="loinfo && loinfo.nmls"> · NMLS {{ (loinfo.nmls || "").trim() }}</span>
          <span class="pf-spacer">·</span>
          <span>All rights reserved</span>
        </div>
      </footer>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandPaper",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    todayDate() {
      return new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Local experts unite<br/>to deliver <em>your dream home</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Source+Serif+4:ital,wght@0,300;0,400;1,300&family=UnifrakturMaguntia&display=swap');

.page {
  min-height: 100vh;
  background: #f5f0e8;
  color: #1a1610;
  font-family: 'Source Serif 4', serif;
  --p: #0b1726;
  --a: #c69c39;
  --ink: #1a1610;
  --bg: #f5f0e8;
  --border: rgba(26,22,16,0.18);
  --muted: #6a6458;
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* MASTHEAD */
.masthead { border-bottom: 0; padding: 0 48px; }
.mh-top { display: flex; align-items: center; justify-content: space-between; padding: 18px 0 12px; gap: 24px; }
.mh-date { font-size: 10px; color: var(--muted); font-style: italic; white-space: nowrap; }
.mh-title-col { text-align: center; flex: 1; }
.mh-logo { max-width: 220px; max-height: 80px; object-fit: contain; display: block; margin: 0 auto 4px; }
.mh-sub { font-size: 9px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--muted); }
.mh-cta { font-family: 'Playfair Display', serif; font-size: 11px; font-weight: 700; text-decoration: none; background: var(--ink); color: var(--bg); padding: 9px 20px; border-radius: 2px; white-space: nowrap; transition: opacity 0.2s; }
.mh-cta:hover { opacity: 0.88; }
.mh-rule-group { display: flex; flex-direction: column; gap: 3px; }
.mhr { width: 100%; }
.mhr-thick { height: 3px; background: var(--ink); }
.mhr-thin { height: 1px; background: var(--ink); opacity: 0.4; }

/* GRID */
.paper-grid { display: grid; grid-template-columns: 1fr 1px 260px 1px 260px; max-width: 1200px; margin: 0 auto; width: 100%; padding: 40px 48px; gap: 0; align-items: start; }
.vert-rule { width: 1px; background: var(--border); margin: 0 32px; align-self: stretch; }

/* MAIN ARTICLE */
.col-main { }
.col-kicker { font-size: 9px; font-weight: 700; letter-spacing: 0.3em; text-transform: uppercase; color: var(--a); margin-bottom: 8px; }
.col-hed-rule { height: 3px; background: var(--ink); margin-bottom: 12px; }
.paper-h1 { font-family: 'Playfair Display', serif; font-size: clamp(32px, 4.5vw, 58px); font-weight: 900; line-height: 1.05; color: var(--ink); margin: 0 0 10px; }
.paper-h1 em { font-style: italic; font-weight: 700; color: color-mix(in srgb, var(--a) 70%, var(--ink)); }
.col-byline { font-size: 11px; font-style: italic; color: var(--muted); margin-bottom: 10px; }
.col-rule { height: 1px; background: var(--border); margin-bottom: 16px; }
.paper-p { font-size: 14px; line-height: 1.88; color: #2a2418; font-weight: 300; margin-bottom: 28px; column-count: 2; column-gap: 24px; }
.paper-btns { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;}
.pp-btn { font-family: 'Playfair Display', serif; font-size: 12px; font-weight: 700; text-decoration: none; background: var(--ink); color: var(--bg); padding: 12px 26px; border-radius: 2px; transition: opacity 0.2s; width: 55%; text-align: center; }
.pp-btn:hover { opacity: 0.88; }
.pp-btn-ghost { font-family: 'Playfair Display', serif; font-size: 12px; text-decoration: none; color: var(--muted); border: 1px solid var(--border); padding: 11px 20px; border-radius: 2px; transition: background 0.18s; }
.pp-btn-ghost:hover { background: rgba(26,22,16,0.05); }

/* PROFILES */
.col-profile { padding: 0 0 0 16px; }
.cp-section-head { font-family: 'Playfair Display', serif; font-size: 11px; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink); border-bottom: 2px solid var(--ink); padding-bottom: 6px; margin-bottom: 14px; }
.re-head { border-color: var(--a); color: color-mix(in srgb, var(--a) 80%, var(--ink)); }
.cp-photo-block { margin-bottom: 12px; border: 1px solid var(--border); padding: 4px; background: #ede8de; }
.lo-photo-block { border-color: rgba(26,22,16,0.2); }
.re-photo-block { border-color: rgba(198,156,57,0.3); }
.cp-photo { width: 100%;  object-fit: cover; display: block; filter: grayscale(15%); }
.cp-caption { font-size: 9px; color: var(--muted); font-style: italic; padding: 4px 2px 0; text-align: center; }
.cp-bio { font-size: 12px; line-height: 1.68; color: #2a2418; margin-bottom: 10px; }
.cp-contacts { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
.cp-link { font-size: 11px; text-decoration: none; font-style: italic; transition: color 0.18s; color: var(--muted); }
.lo-cp-link:hover { color: var(--ink); }
.re-cp-link:hover { color: color-mix(in srgb, var(--a) 80%, var(--ink)); }
.cp-apply { display: block; text-align: center; text-decoration: none; font-family: 'Playfair Display', serif; font-size: 11px; font-weight: 700; padding: 10px; border: 2px solid var(--ink); color: var(--ink); transition: background 0.2s, color 0.2s; }
.cp-apply:hover { background: var(--ink); color: var(--bg); }

/* FOOTER */
.paper-footer { padding: 16px 48px; margin-top: auto; }
.pf-rules { display: flex; flex-direction: column; gap: 3px; margin-bottom: 10px; }
.pf-credits { font-size: 9px; color: var(--muted); display: flex; flex-wrap: wrap; gap: 4px; }
.pf-spacer { margin: 0 8px; }

@media (max-width: 900px) {
  .paper-grid { grid-template-columns: 1fr; padding: 24px; }
  .vert-rule { display: none; }
  .col-profile { padding: 0; }
  .paper-p { column-count: 1; }
  .masthead { padding: 0 24px; }
  .paper-footer { padding: 14px 24px; }
}
</style>
